/** One party's bank accounts, paged with "load more"; inactive and terminal ones on request. */
import { useCallback } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { PartyBankAccountsDocument, type PartyBankAccountFieldsFragment } from '@/graphql';
import { DEFAULT_BANK_ACCOUNTS_FILTER } from '../utils/bank-account-actions';

export const BANK_ACCOUNTS_PAGE_SIZE = 25;

export type PartyBankAccount = PartyBankAccountFieldsFragment;

export function usePartyBankAccounts(partyId: string, showAll: boolean) {
  const { data, loading, error, fetchMore, networkStatus } = useQuery(PartyBankAccountsDocument, {
    variables: {
      partyId,
      filter: showAll ? null : DEFAULT_BANK_ACCOUNTS_FILTER,
      first: BANK_ACCOUNTS_PAGE_SIZE
    },
    notifyOnNetworkStatusChange: true
  });

  const connection = data?.partyBankAccounts;
  const accounts: readonly PartyBankAccount[] = connection?.edges.map(edge => edge.node) ?? [];
  const nextCursor = connection?.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;

  // Pages are merged by the relayStylePagination cache policy — no updateQuery needed.
  const loadMore = useCallback(async () => {
    if (nextCursor) await fetchMore({ variables: { after: nextCursor } });
  }, [fetchMore, nextCursor]);

  return {
    accounts,
    hasMore: nextCursor !== null,
    loadMore,
    loading: loading && accounts.length === 0,
    loadingMore: networkStatus === NetworkStatus.fetchMore,
    error
  };
}
